import {
  UserCircle,
  Factory,
  Scale,
  Landmark,
  Building2,
  Headset,
  Briefcase,
  ShieldCheck,
  Languages,
  MessageSquareText,
  Ban,
  Plus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface KIProfileScreenProps {
  className?: string;
}

interface Profile {
  name: string;
  description: string;
  icon: LucideIcon;
  active?: boolean;
}

const C = {
  surface: 'var(--bnkp-surface, #141B27)',
  elevated: 'var(--bnkp-elevated, #1A2334)',
  secondary: 'var(--bnkp-recessed, #0A0F1C)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  text: 'var(--bnkp-text, #E2E8F0)',
  textSec: 'var(--bnkp-text2, #94A3C2)',
  textTer: 'var(--bnkp-text3, #4A5568)',
  primary: 'var(--bnkp-primary, #2D7EFF)',
  green: 'var(--bnkp-green, #34D399)',
} as const;

export function KIProfileScreen({ className }: KIProfileScreenProps) {
  const { t } = useTranslation('demo');

  const PROFILES: Profile[] = [
    { name: t('kiprofile.profileStandardName'), description: t('kiprofile.profileStandardDesc'), icon: UserCircle, active: true },
    { name: t('kiprofile.profileMaschinenbauName'), description: t('kiprofile.profileMaschinenbauDesc'), icon: Factory },
    { name: t('kiprofile.profileRechtName'), description: t('kiprofile.profileRechtDesc'), icon: Scale },
    { name: t('kiprofile.profileFinanzenName'), description: t('kiprofile.profileFinanzenDesc'), icon: Briefcase },
    { name: t('kiprofile.profileOeffentlichName'), description: t('kiprofile.profileOeffentlichDesc'), icon: Landmark },
    { name: t('kiprofile.profileKundenserviceName'), description: t('kiprofile.profileKundenserviceDesc'), icon: Headset },
    { name: t('kiprofile.profileVertriebName'), description: t('kiprofile.profileVertriebDesc'), icon: Building2 },
    { name: t('kiprofile.profileItSecurityName'), description: t('kiprofile.profileItSecurityDesc'), icon: ShieldCheck },
  ];

  const SETTINGS: { label: string; value: string; icon: LucideIcon }[] = [
    { label: t('kiprofile.settingSpracheLabel'), value: t('kiprofile.settingSpracheValue'), icon: Languages },
    { label: t('kiprofile.settingTonalitaetLabel'), value: t('kiprofile.settingTonalitaetValue'), icon: MessageSquareText },
    { label: t('kiprofile.settingTabuLabel'), value: t('kiprofile.settingTabuValue'), icon: Ban },
    { label: t('kiprofile.settingAntwortsprachenLabel'), value: t('kiprofile.settingAntwortsprachenValue'), icon: Languages },
  ];

  return (
    <section
      className={`kip-root${className ? ` ${className}` : ''}`}
      style={{ fontFamily: 'Inter, sans-serif', color: C.text }}
    >
      <style>{`
        .kip-root { display: flex; flex-direction: column; gap: 20px; }
        .kip-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
        @media (min-width: 720px) { .kip-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
        .kip-card { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; padding: 16px; cursor: pointer; transition: border-color .18s, background .18s, transform .18s; }
        .kip-card:hover { background: rgba(255,255,255,0.04); border-color: ${C.borderStrong}; transform: translateY(-1px); }
        .kip-card.kip-active { border-color: ${C.primary}; background: ${C.elevated}; box-shadow: 0 0 0 1px ${C.primary}, 0 0 22px rgba(45,126,255,0.28); }
        .kip-iconpill { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 10px; background: rgba(45,126,255,0.10); color: ${C.primary}; }
        .kip-detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
        @media (min-width: 720px) { .kip-detail-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
        .kip-dot { width: 8px; height: 8px; border-radius: 50%; background: ${C.green}; box-shadow: 0 0 0 0 rgba(52,211,153,0.55); animation: kip-pulse 2s infinite; }
        @keyframes kip-pulse { 0% { box-shadow: 0 0 0 0 rgba(52,211,153,0.5); } 70% { box-shadow: 0 0 0 7px rgba(52,211,153,0); } 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <span className="kip-iconpill" aria-hidden="true">
            <UserCircle size={22} />
          </span>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>{t('kiprofile.headerTitle')}</h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textSec }}>
              {t('kiprofile.headerSubtitle')}
            </p>
          </div>
        </div>
        <button
          type="button"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
            color: C.text, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
          }}
        >
          <Plus size={15} /> {t('kiprofile.newProfileButton')}
        </button>
      </div>

      {/* Profile grid */}
      <div className="kip-grid">
        {PROFILES.map((p) => {
          const Icon = p.icon;
          return (
            <button
              key={p.name}
              type="button"
              className={`kip-card${p.active ? ' kip-active' : ''}`}
              style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 10 }}
              aria-pressed={p.active}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="kip-iconpill" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span
                  style={{
                    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                    padding: '3px 8px', borderRadius: 999,
                    color: p.active ? C.primary : C.textTer,
                    background: p.active ? 'rgba(45,126,255,0.14)' : 'transparent',
                    border: p.active ? '1px solid rgba(45,126,255,0.35)' : '1px solid transparent',
                  }}
                >
                  {p.active ? t('kiprofile.badgeActive') : t('kiprofile.badgeStandard')}
                </span>
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: C.text }}>{p.name}</div>
              <div style={{ fontSize: 12, color: C.textSec, lineHeight: 1.35 }}>{p.description}</div>
            </button>
          );
        })}
      </div>

      {/* Active profile detail panel */}
      <div style={{ background: C.surface, border: '1px solid ' + C.border, borderRadius: 16, padding: 18, boxShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span className="kip-dot" aria-hidden="true" />
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>
            {t('kiprofile.activeProfileHeading')}
          </h2>
        </div>
        <div className="kip-detail-grid">
          {SETTINGS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                style={{ background: C.secondary, border: '1px solid ' + C.border, borderRadius: 12, padding: 14 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: C.textSec, fontSize: 11.5, marginBottom: 8 }}>
                  <Icon size={14} /> {s.label}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                  {s.label === t('kiprofile.settingAntwortsprachenLabel')
                    ? `${s.value} ${t('kiprofile.settingAntwortsprachenLabel')}`
                    : s.value}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p style={{ margin: 0, fontSize: 12, color: C.textTer, textAlign: 'center' }}>
        {t('kiprofile.footerNote')}
      </p>
    </section>
  );
}
